import { LiveOhtScheduler } from '../features/simulation/liveOhtScheduler';
import type {
  AmhsLiveInventoryGate,
  LiveOhtRuntimeFrame,
  LiveOhtRuntimeSnapshot,
  OhtTimelineEvent,
} from '../types/amhsStream';
import { simulationFrameBus } from './simulationFrameBus';
import type { DerivedGateModule } from '../utils/derivedGateEquipment';
import type { LiveRailIndex } from '../features/simulation/liveRailPose';

type Listener = (frame: LiveOhtRuntimeFrame) => void;

type IndexedTransfer = {
  runtime: LiveOhtRuntimeSnapshot;
  order: number;
};

export type ActiveTransferQuery = {
  portIds?: Iterable<string>;
  stationIds?: Iterable<string>;
  /**
   * Equipment aliases to match against both transfer.stationId and the owner
   * encoded by a standard `...-LP-N` port id.
   */
  stationAliases?: Iterable<string>;
};

const PORT_OWNER_SUFFIX = /^(.*)-LP-\d+$/;

const canonicalStationId = (value: string): string =>
  value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

/**
 * Per-frame active-transfer directory. Values retain frame/OHT order so a
 * multi-key query has the same winner as Object.values(byOhtId).find(...).
 */
export class LiveOhtActiveTransferIndex {
  private frame: LiveOhtRuntimeFrame | null = null;
  private byPortId = new Map<string, IndexedTransfer>();
  private byStationId = new Map<string, IndexedTransfer>();
  private byStationAlias = new Map<string, IndexedTransfer>();

  find(
    frame: LiveOhtRuntimeFrame,
    query: ActiveTransferQuery
  ): LiveOhtRuntimeSnapshot | null {
    this.ensureFrame(frame);
    let winner: IndexedTransfer | undefined;
    const consider = (candidate: IndexedTransfer | undefined) => {
      if (candidate && (!winner || candidate.order < winner.order)) winner = candidate;
    };

    for (const portId of query.portIds ?? []) consider(this.byPortId.get(portId));
    for (const stationId of query.stationIds ?? []) {
      consider(this.byStationId.get(stationId));
    }
    for (const alias of query.stationAliases ?? []) {
      consider(this.byStationAlias.get(canonicalStationId(alias)));
    }
    return winner?.runtime ?? null;
  }

  private ensureFrame(frame: LiveOhtRuntimeFrame): void {
    if (this.frame === frame) return;
    this.frame = frame;
    this.byPortId.clear();
    this.byStationId.clear();
    this.byStationAlias.clear();

    Object.values(frame.byOhtId).forEach((runtime, order) => {
      const transfer = runtime.transfer;
      if (!transfer) return;
      const indexed = { runtime, order };
      this.setFirst(this.byPortId, transfer.portId, indexed);
      if (transfer.stationId) {
        this.setFirst(this.byStationId, transfer.stationId, indexed);
        this.setFirst(
          this.byStationAlias,
          canonicalStationId(transfer.stationId),
          indexed
        );
      }
      const portOwnerId = PORT_OWNER_SUFFIX.exec(transfer.portId)?.[1];
      if (portOwnerId) {
        this.setFirst(
          this.byStationAlias,
          canonicalStationId(portOwnerId),
          indexed
        );
      }
    });
  }

  private setFirst(
    index: Map<string, IndexedTransfer>,
    key: string,
    value: IndexedTransfer
  ): void {
    if (key && !index.has(key)) index.set(key, value);
  }
}

class LiveOhtRuntimeStore {
  private readonly scheduler = new LiveOhtScheduler();
  private readonly activeTransfers = new LiveOhtActiveTransferIndex();
  private listeners = new Set<Listener>();

  constructor() {
    simulationFrameBus.subscribe((simTimeS, wallDtS) => {
      const frame = this.scheduler.tick(simTimeS, wallDtS);
      for (const listener of this.listeners) listener(frame);
    }, 'schedule');
  }

  setTimelines(timelines: Record<string, OhtTimelineEvent[]>): void {
    this.scheduler.setTimelines(timelines);
  }

  setGateInventory(gates: readonly AmhsLiveInventoryGate[]): void {
    this.scheduler.setGateInventory(gates);
  }

  setGateModules(modules: readonly DerivedGateModule[]): void {
    this.scheduler.setGateModules(modules);
  }

  setRailIndex(index: LiveRailIndex | null): void {
    this.scheduler.setRailIndex(index);
  }

  getFrame(): LiveOhtRuntimeFrame {
    return this.scheduler.getFrame();
  }

  getOht(ohtId: string): LiveOhtRuntimeSnapshot | null {
    return this.scheduler.getFrame().byOhtId[ohtId] ?? null;
  }

  getActiveTransfer(query: ActiveTransferQuery): LiveOhtRuntimeSnapshot | null {
    const frame = this.scheduler.getFrame();
    return this.activeTransfers.find(frame, query);
  }

  reset(): void {
    this.scheduler.reset();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
}

export const liveOhtRuntimeStore = new LiveOhtRuntimeStore();
